import { useContext, useEffect, useState } from 'react';

import { ReloadOutlined } from '@ant-design/icons';
import {
	Button,
	Checkbox,
	Flex,
	Input,
	Space,
	Spin,
	Table,
	Tooltip,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import { SubscriptionContext } from '@/App';
import { useGetAllCopilotVideos } from '@/api/batch_process';
import MultiButton from '@/components/common/MultiButton';
import { SUBSCRIPTIONS_TYPES_ENUM } from '@/const';
import { useDebounce } from '@/hooks';
import hasSubscriptionType from '@/lib/helpers/hasSubscriptionType';
import { PageContext } from '@/providers/PageContextProvider';

import QADrawer from '../../qa-collections/QADrawer';
import { IPagination } from '../../video-intelligence-on-demand';
import FiltersSection from './../FiltersSection';
import columns from './data';

export default function CopilotSingleVideos() {
	const navigate = useNavigate();
	const { subscriptions, subscriptionLoading } =
		useContext(SubscriptionContext);
	const {
		setPageTitle,
		openDrawer,
		closeDrawer,
		setIsCopilotCreditsCounterVisible,
	} = useContext(PageContext);
	const [appliedData, setAppliedData] = useState<any>(null);
	const [pagination, setPagination] = useState<IPagination>({
		page: 1,
		pageCount: 1,
		total: 0,
		limit: 10,
	});
	const [filterUserInsights, setFilterUserInsights] = useState(
		localStorage?.getItem('sv_show_user_insights') === '1' ? true : false,
	);
	const [onlyFavourites, setOnlyFavourites] = useState(
		localStorage?.getItem('sv_only_favourites') === '1' ? true : false,
	);
	const [search, setSearch] = useState('');
	const debouncedSearch = useDebounce(search);
	const [sortBy, setSortBy] = useState<'requested_at'>('requested_at');
	const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
	const [isPanelOpen, setIsPanelOpen] = useState(false);

	const { data, isLoading, isFetching, refetch } = useGetAllCopilotVideos({
		page: pagination.page,
		limit: pagination.limit,
		onlyUser: filterUserInsights,
		favourites: onlyFavourites,
		sortBy,
		sortOrder,
		search: debouncedSearch.length >= 3 ? debouncedSearch : '',
	});

	const total = data?.data?.data?.pagination?.total ?? 1;
	const items = data?.data?.data?.items ?? [];

	useEffect(() => {
		setPageTitle('Video insights co-pilot');
		setIsCopilotCreditsCounterVisible(true);

		return () => {
			setPageTitle('');
			setIsCopilotCreditsCounterVisible(false);
		};
	}, []);

	useEffect(() => {
		refetch();
	}, [filterUserInsights, sortOrder, pagination]);

	useEffect(() => {
		if (debouncedSearch.length >= 3) {
			setPagination((cur) => ({ ...cur, page: 1 }));
		}
	}, [debouncedSearch]);

	useEffect(() => {
		if (!subscriptionLoading && subscriptions && subscriptions?.length > 0) {
			if (
				!hasSubscriptionType(subscriptions, SUBSCRIPTIONS_TYPES_ENUM.copilot)
			) {
				navigate('/app/dashboard/product-main-menu');
			}
		} else if (
			!subscriptionLoading &&
			subscriptions &&
			subscriptions?.length === 0
		) {
			navigate('/app/dashboard/product-main-menu');
		}
	}, [subscriptions, subscriptionLoading]);

	const onFavouriteChange = () => {
		setOnlyFavourites((prev) => {
			localStorage.setItem('sv_only_favourites', !prev ? '1' : '0');
			return !prev;
		});
		setPagination((cur: any) => ({ ...cur, page: 1 }));
	};

	const onUserInsightsChange = () => {
		setFilterUserInsights((prev) => {
			localStorage.setItem('sv_show_user_insights', !prev ? '1' : '0');
			return !prev;
		});

		setPagination((cur: any) => ({ ...cur, page: 1 }));
	};

	const onTableChange = (__: any, _: any, sorter: any) => {
		const { field = 'requested_at', order = 'ascend' } = sorter;

		if (field) {
			setSortBy(field);
		}

		if (order) {
			setSortOrder(order === 'ascend' ? 'asc' : 'desc');
		}
	};

	const onApplyQuestionData = (data: any) => {
		setAppliedData(data);
		closeDrawer();
		setIsPanelOpen(true);
	};

	return (
		<>
			<Spin spinning={isLoading || isFetching}>
				<div>
					<Flex vertical gap={16} style={{ width: '100%' }}>
						<Flex
							gap={16}
							justify="flex-end"
							align="center"
							style={{ width: '100%' }}
						>
							<Tooltip title="Refresh">
								<Button onClick={() => refetch()} icon={<ReloadOutlined />} />
							</Tooltip>
						</Flex>

						<Flex
							gap={16}
							justify="space-between"
							align="center"
							style={{ width: '100%' }}
						>
							<Input
								placeholder="Search videos..."
								style={{ width: '300px' }}
								onChange={(e) => setSearch(e.target.value)}
								value={search}
							/>

							<Flex gap={16}>
								<Checkbox
									checked={filterUserInsights}
									onChange={onUserInsightsChange}
								>
									my reports
								</Checkbox>
								<Checkbox checked={onlyFavourites} onChange={onFavouriteChange}>
									favourites
								</Checkbox>
							</Flex>
						</Flex>

						<Table
							loading={isLoading || isFetching}
							// style={{ width: '100%' }}
							onChange={onTableChange}
							dataSource={items}
							columns={[
								...columns,
								{
									title: 'Actions',
									dataIndex: 'actions',
									key: 'actions',
									width: 40,
									ellipsis: false,
									fixed: 'right',
									// flex: true,
									render: (_: any, record: any) => {
										return (
											<Flex
												justify="center"
												align="center"
												style={{ gap: '8px' }}
											>
												<MultiButton
													record={record}
													keys={[
														'view',
														'share',
														'favourite',
														'collection',
														'archive',
													]}
												/>
											</Flex>
										);
									},
								},
							]}
							rowKey={'id'}
							bordered
							scroll={{ x: 1100 }}
							pagination={{
								total,
								showSizeChanger: true,
								current: pagination.page,
								pageSizeOptions: [10, 25, 50],
								onChange: (page: number, pageSize: number) => {
									setPagination((cur) => ({ ...cur, page, limit: pageSize }));
								},
							}}
							rowClassName={(record) =>
								record?.status < 2 || record?.status >= 3 ? 'highlight-row' : ''
							}
						/>
					</Flex>
				</div>
				<QADrawer onApplyQuestionData={onApplyQuestionData} />
			</Spin>
		</>
	);
}
